import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const galleryImages = [
  {
    src: "https://customer-assets.emergentagent.com/job_tap-pro-hub/artifacts/yo96b06l_Photoroom_20260423_085118.jpeg",
    alt: "Tibs Taps service vehicles",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_tap-pro-hub/artifacts/c0rysz7s_IMG_0574.jpeg",
    alt: "Draft beer tap lineup",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_tap-pro-hub/artifacts/5rl82uz2_IMG_0572.jpeg",
    alt: "Bar tap system installation",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_tap-pro-hub/artifacts/tg364ayq_IMG_0571.jpeg",
    alt: "Brick wall tap system with glassware",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_tap-pro-hub/artifacts/zq027ocl_IMG_0573.jpeg",
    alt: "Wall-mounted tap system installation",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_tap-pro-hub/artifacts/2d6438pg_IMG_0575.jpeg",
    alt: "Bar tap system with Tibs Taps branding",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_tap-pro-hub/artifacts/rxg0d35v_IMG_0794.jpeg",
    alt: "Barrel tap system with craft beer handles",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_tap-pro-hub/artifacts/hxzr3v5k_IMG_0793.jpeg",
    alt: "Multi-tap draft system with stone wall",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_tap-pro-hub/artifacts/gbt8kcj6_IMG_0791.jpeg",
    alt: "Full tap lineup installation with stone backdrop",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_tap-pro-hub/artifacts/kpllo7oo_1000003574.jpeg",
    alt: "Outdoor patio tap tower with craft beer handles",
  },
];

const SLIDE_INTERVAL_MS = 4000;

export default function GallerySection() {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % galleryImages.length);
    }, SLIDE_INTERVAL_MS);
  }, []);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  const goTo = (index) => {
    setCurrent(index);
    startTimer();
  };

  const openImage = () => {
    window.open(galleryImages[current].src, "_blank");
  };

  const prev = () => goTo((current - 1 + galleryImages.length) % galleryImages.length);
  const next = () => goTo((current + 1) % galleryImages.length);

  return (
    <section id="gallery" className="py-24 bg-[#0A0A0A]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="overline mb-4" data-testid="gallery-overline">Photo Gallery</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-4" data-testid="gallery-heading">
            <span className="gold-text">Photo Gallery</span>
          </h2>
        </div>

        <div className="relative">
          {/* Main Image */}
          <div
            className="relative overflow-hidden rounded-2xl border border-[#27272A] bg-[#141414] aspect-[16/9] sm:aspect-[2/1] cursor-pointer"
            onClick={openImage}
          >
            {galleryImages.map((image, index) => (
              <img
                key={image.src}
                src={image.src}
                alt={image.alt}
                loading="lazy"
                className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-700 ${
                  index === current ? "opacity-100" : "opacity-0"
                }`}
                data-testid={`gallery-image-${index}`}
              />
            ))}
            <div className="absolute bottom-4 left-0 right-0 text-center pointer-events-none">
              <span className="text-white/50 text-xs">Tap image to enlarge</span>
            </div>
          </div>

          {/* Prev/Next Buttons */}
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-[#D4AF37] hover:text-black transition-colors"
            data-testid="gallery-prev"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-[#D4AF37] hover:text-black transition-colors"
            data-testid="gallery-next"
            aria-label="Next image"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {galleryImages.map((img, index) => (
              <button
                key={img.src}
                onClick={() => goTo(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === current ? "bg-[#D4AF37]" : "bg-[#27272A]"
                }`}
                data-testid={`gallery-dot-${index}`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
